// MA-57: teacher route — the routing target for a signed-in teacher.
// MA-61 slice 1: class creation + name-only roster (real, not a placeholder).
// NOT built here (follow-up sub-slices): no-email student login, class
// dashboard/rollup, assign-at-class-level, Misconception Receipt teacher
// view — per docs/build/specs/SPEC-classroom-mode.md.
//
// Gated identically to /parent (src/routes/parent.tsx): the acceptance
// text for MA-57 requires a "grown-up gate guarding parent/teacher" — a
// kid who deep-links /teacher directly must not reach it without clearing
// the same math-challenge gate that protects /parent. Personal-device mode
// auto-bypasses (the device IS the teacher's own device), exactly mirroring
// parent.tsx's bypass. This duplicates parent.tsx's ~10-line gate block
// rather than extracting a shared hook — two call sites, per this repo's
// no-premature-abstraction convention (CLAUDE.md); revisit if a third
// consumer appears.
//
// MA-546 — /teacher navigation. Pre-slice, the ONLY navigation off this
// route was ParentGate's own cancel button — a teacher who landed here had
// no way back to /parent. BUILD: a slim header (wordmark + a "Parent
// area" link, MA-971) wrapping the existing wordmark line below. Presentation
// only — no new capability, MA-61 data machinery (roster/consent/assign)
// byte-unchanged.
//
// MA-924 (TUX-1) — this route file now ONLY composes: the grown-up gate below
// (unchanged) around the teacher shell, whose header, four tabs and wide main
// region live in src/screens/teacher/TeacherShell.tsx and whose sections live
// in src/screens/teacher/{TodayTab,StudentsTab,FamiliesTab,SetupTab}.tsx
// (TEACHER-UX-2026-10-09 D-TUX-1).
//
// MA-1056 — the website entry /teacher/start forwards here with ?tab=setup
// (src/routes/teacher_.start.tsx). Once this route opens (signed in, past the
// beta gate) the pending entry has done its job and is cleared, so the front
// door goes back to its role default; the gate below is unchanged.
import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { isParentGateUnlocked, onHandoffArmedElsewhere, personalBypassAllowed, unlockParentGate } from "@/lib/parentGate";
import { getDeviceMode } from "@/lib/deviceMode";
import { ParentGate } from "@/components/ParentGate";
import { TeacherShell } from "@/screens/teacher/TeacherShell";
import { clearPendingTeacherStart } from "@/lib/teacher/pendingTeacherStart";

function TeacherAreaGated() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    clearPendingTeacherStart();
  }, []);

  useEffect(() => {
    // MA-855: unless the device was handed to a child ("Hand to kid") and
    // no grown-up has passed the gate since — then it asks, like a kiosk.
    if (personalBypassAllowed(getDeviceMode())) {
      // Teacher's own device — skip the kid-lock and stamp the session
      // unlock so any downstream isParentGateUnlocked() check is satisfied.
      unlockParentGate();
      setUnlocked(true);
    } else {
      setUnlocked(isParentGateUnlocked());
    }
  }, []);

  // MA-855 RED r1 SHOULD-1: a "Hand to kid" in another tab closes this one.
  useEffect(() => onHandoffArmedElsewhere(() => setUnlocked(false)), []);

  if (!unlocked) {
    return (
      <ParentGate
        onUnlock={() => setUnlocked(true)}
        onCancel={() => navigate({ to: "/play" })}
      />
    );
  }

  return (
    <div
      className="sb-root flex min-h-dvh flex-col items-center bg-background px-6 py-10 text-center font-sans text-foreground"
      data-testid="teacher-area"
    >
      <TeacherShell />
    </div>
  );
}

export const Route = createFileRoute("/teacher")({ component: TeacherAreaGated });
